import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import DynamicTable from "../../../components/DynamicTable";
import { ProductPerformance } from "./productTypes";
import { formatCurrency } from "../FinancialDashboardController";
import ExportProductsButton from "./ExportProductsButton";
import "./ProductPerformanceTable.scss";

interface Props {
  performances: ProductPerformance[];
  selectedProducts: string[];
  onProductSelect: (productCode: string) => void;
  maxProductsReached: boolean;
  fromDate: Date | null;
  toDate: Date | null;
}

const ProductPerformanceTable: React.FC<Props> = ({
  performances,
  selectedProducts,
  onProductSelect,
  maxProductsReached,
  fromDate,
  toDate,
}) => {
  const { t } = useTranslation();

  const columns = useMemo(
    () => [
      {
        Header: t("productsdashboard.table.actions"),
        id: "actions",
        disableSortBy: true,
        Cell: ({ row }: any) => {
          const productCode = row.original.ProductCode;
          const isSelected = selectedProducts.includes(productCode);
          const canSelect = !maxProductsReached || isSelected;

          return (
            <button
              className={`select-btn ${isSelected ? "selected" : ""}`}
              onClick={() => onProductSelect(productCode)}
              disabled={!canSelect && !isSelected}
              title={
                isSelected
                  ? t("productsdashboard.table.deselect").toString()
                  : !canSelect
                    ? t("productsdashboard.max-products-reached").toString()
                    : t("productsdashboard.select-for-comparison").toString()
              }
            >
              {isSelected
                ? t("productsdashboard.table.selected")
                : t("productsdashboard.table.select")}
            </button>
          );
        },
      },
      {
        Header: t("productsdashboard.table.product-code"),
        accessor: "ProductCode",
      },
      {
        Header: t("productsdashboard.table.product-title"),
        accessor: "ProductTitle",
      },
      {
        Header: t("productsdashboard.table.total-units"),
        accessor: "TotalUnits",
        Cell: ({ value }: any) => value.toLocaleString(),
      },
      {
        Header: t("productsdashboard.table.vat"),
        accessor: "VAT",
        Cell: ({ value }: any) => `${value}%`,
      },
      {
        Header: t("productsdashboard.table.total-revenue"),
        accessor: "TotalRevenue",
        Cell: ({ value }: any) => formatCurrency(value),
      },
      {
        Header: t("productsdashboard.table.revenue-ex-vat"),
        accessor: "TotalRevenueExVAT",
        Cell: ({ value }: any) => formatCurrency(value),
      },
      {
        Header: t("productsdashboard.table.vat-amount"),
        id: "VATAmount",
        accessor: (row: ProductPerformance) =>
          row.TotalRevenue - row.TotalRevenueExVAT,
        Cell: ({ value }: any) => formatCurrency(value),
      },
      {
        Header: t("productsdashboard.table.avg-units-period"),
        accessor: "AvgUnitsPerPeriod",
        Cell: ({ value }: any) => value.toFixed(1),
      },
      {
        Header: t("productsdashboard.table.avg-revenue-period"),
        accessor: "AvgRevenuePerPeriod",
        Cell: ({ value }: any) => formatCurrency(value),
      },
    ],
    [t, selectedProducts, onProductSelect, maxProductsReached],
  );

  return (
    <div className="product-performance-table-section">
      <div className="table-header">
        <h2>{t("productsdashboard.table.title")}</h2>
        <ExportProductsButton
          performances={performances}
          fromDate={fromDate}
          toDate={toDate}
        />
      </div>
      <DynamicTable
        className="ProductPerformance"
        columns={columns}
        data={performances}
        loading={false}
        globalFilter={true}
        showActiveField={false}
        initialState={{
          pageSize: 20,
          sortBy: [{ id: "TotalRevenue", desc: true }],
        }}
      />
    </div>
  );
};

export default ProductPerformanceTable;
